import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../config';
import './Auth.css';

axios.defaults.withCredentials = true;

const ResetPassword = () => {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const startTimer = () => {
    setOtpTimer(60);
    const interval = setInterval(() => {
      setOtpTimer(prev => {
        if (prev <= 1) clearInterval(interval);
        return prev - 1;
      });
    }, 1000);
  };

  const handleRequestOTP = async () => {
    setError('');
    setLoading(true);

    if (!email.trim()) {
      setError('Please enter your email address');
      setLoading(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      await axios.post(
        `${API_BASE_URL}/auth/request-otp`,
        { email },
        { headers: { 'Content-Type': 'application/json' } }
      );
      setStep(2);
      startTimer();
    } catch (err) {
      if (err.response) {
        setError(err.response.data?.message || 'Failed to send OTP. Check your email.');
      } else if (err.request) {
        setError('Could not connect to server. Check backend is running.');
      } else {
        setError('Error: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    setError('');
    setLoading(true);

    if (!otp.trim() || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      setLoading(false);
      return;
    }

    try {
      await axios.post(
        `${API_BASE_URL}/auth/verify-otp`,
        { email, otp },
        { headers: { 'Content-Type': 'application/json' } }
      );
      setStep(3);
    } catch (err) {
      if (err.response) {
        setError(err.response.data?.message || 'Invalid or expired OTP');
      } else if (err.request) {
        setError('Could not connect to server. Check backend is running.');
      } else {
        setError('Error: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (!newPassword.trim() || !confirmPassword.trim()) {
      setError('Please fill in both password fields');
      return;
    }

    setError('');
    setLoading(true);
    try {
      await axios.post(
        `${API_BASE_URL}/auth/reset-password`,
        { email, otp, newPassword },
        { headers: { 'Content-Type': 'application/json' } }
      );
      alert('Password reset successful! Please login with your new password.');
      navigate('/login');
    } catch (err) {
      if (err.response) {
        setError(err.response.data?.message || 'Failed to reset password');
      } else if (err.request) {
        setError('Could not connect to server. Check backend is running.');
      } else {
        setError('Error: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-wrapper">
      <div className="auth-background">
        <div className="bg-shapes">
          <div className="bg-shape bg-shape-1"></div>
          <div className="bg-shape bg-shape-2"></div>
          <div className="bg-shape bg-shape-3"></div>
        </div>
      </div>
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="brand-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
              </svg>
            </div>
            <h1 className="auth-title">Reset Password</h1>
            <p className="auth-subtitle">We'll send you a verification code</p>
          </div>

          {step === 1 && (
            <div className="auth-form">
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <div className="input-wrapper">
                  <svg className="input-icon" viewBox="0 0 24 24">
                    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                  <input
                    type="email"
                    className="form-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="name@company.com"
                  />
                </div>
              </div>
              {error && <div className="error-box">{error}</div>}
              <button 
                type="button" 
                className="auth-btn"
                disabled={loading || !email}
                onClick={handleRequestOTP}
              >
                {loading ? 'Sending...' : 'Send Verification Code'}
              </button>
              <div className="auth-links">
                <span className="auth-link" onClick={() => navigate('/login')}>
                  <svg viewBox="0 0 24 24"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg>
                  Back to Login
                </span>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="auth-form">
              <div className="step-indicator">
                <span className="step-dot active">1</span>
                <span className="step-line"></span>
                <span className="step-dot active">2</span>
                <span className="step-line"></span>
                <span className="step-dot">3</span>
              </div>
              <p className="step-info">Enter the 6-digit code sent to your email</p>
              <div className="email-display">{email}</div>
              <div className="form-group">
                <label className="form-label">Verification Code</label>
                <input
                  type="text"
                  className="form-input otp-input"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  required
                  placeholder="000000"
                  maxLength={6}
                />
              </div>
              {otpTimer > 0 && (
                <p className="timer-text">Resend code in {otpTimer}s</p>
              )}
              {error && <div className="error-box">{error}</div>}
              <button 
                type="button" 
                className="auth-btn"
                disabled={loading || !otp || otp.length !== 6}
                onClick={handleVerifyOTP}
              >
                {loading ? 'Verifying...' : 'Verify Code'}
              </button>
              <div className="auth-links">
                <span className="auth-link" onClick={() => setStep(1)}>
                  <svg viewBox="0 0 24 24"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg>
                  Change Email
                </span>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="auth-form">
              <div className="step-indicator">
                <span className="step-dot active">1</span>
                <span className="step-line"></span>
                <span className="step-dot active">2</span>
                <span className="step-line"></span>
                <span className="step-dot active">3</span>
              </div>
              <p className="step-info">Create a new password for your account</p>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <div className="input-wrapper password-wrapper">
                  <svg className="input-icon" viewBox="0 0 24 24">
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                  </svg>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-input"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    placeholder="Min 6 characters"
                    minLength={6}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <svg viewBox="0 0 24 24"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>
                    ) : (
                      <svg viewBox="0 0 24 24"><path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/></svg>
                    )}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Confirm Password</label>
                <div className="input-wrapper">
                  <svg className="input-icon" viewBox="0 0 24 24">
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                  </svg>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    placeholder="Re-enter password"
                    minLength={6}
                  />
                </div>
              </div>
              {error && <div className="error-box">{error}</div>}
              <button 
                type="button" 
                className="auth-btn"
                disabled={loading || !newPassword || !confirmPassword}
                onClick={handleResetPassword}
              >
                {loading ? 'Resetting...' : 'Update Password'}
              </button>
            </div>
          )}
        </div>
        <p className="auth-footer">&copy; 2024 Dewan-e-Khass. All rights reserved.</p>
      </div>
    </div>
  );
};

export default ResetPassword;